// Manager-only Labour Factor management (WCO-2474, standard-based model).
// Pick a finished product → table of its raw-material inputs. Each input's throughput
// (lb per worker-hour) is editable; the Labour Factor (1.x) is DERIVED read-only and
// recomputed LIVE in the browser as baseline_throughput ÷ row_throughput (2dp), mirroring
// the backend LF_DERIVED_SQL. One input per product is the baseline (radio → LF = 1.0).
// Editing a non-baseline input's lb/hr changes only its own LF; editing the baseline input's
// lb/hr recomputes every input's LF. Until a baseline is chosen, all LFs render "—".

import React, { useState, useMemo, useEffect } from 'react';
import { useQueryClient } from 'react-query';
import { Search, Scale, ArrowRightLeft, X, History } from 'lucide-react';
import productionService, { LabourFactorMaterial, LabourFactorHistoryEntry } from '../../services/productionService';
import { formatDateTimeLocal } from '../../utils/dateUtils';
import inputOutputMappingService, { InputOutputProduct } from '../../services/inputOutputMappingService';
import { useLanguage } from '../../i18n/LanguageContext';
import { useAuth } from '../../hooks/useAuth';

// Parse a throughput text field to a positive number, or null when empty/invalid.
const parseThroughput = (value: string): number | null => {
  const trimmed = (value ?? '').trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  return Number.isFinite(n) && n > 0 ? n : null;
};

// WCO-2544: parse an allowance % field to a 0–100 number (empty → 0), or null when out of range.
const parseAllowance = (value: string): number | null => {
  const trimmed = (value ?? '').trim();
  if (trimmed === '') return 0;
  const n = Number(trimmed);
  return Number.isFinite(n) && n >= 0 && n <= 100 ? n : null;
};

// Live Labour Factor derivation — the single source of truth for the LF column.
// Mirrors backend LF_DERIVED_SQL: null when no baseline or the row throughput is null/0,
// otherwise baseline ÷ material rounded to 2 decimals.
export const deriveLabourFactor = (
  baselineThroughput: number | null,
  rowThroughput: number | null
): number | null => {
  if (baselineThroughput == null || rowThroughput == null || rowThroughput === 0) return null;
  return Math.round((baselineThroughput / rowThroughput) * 100) / 100;
};

// Editable row = server row + the in-progress text of the lb/hr and allowance-% fields.
interface EditableRow extends LabourFactorMaterial {
  _input: string;
  _allowance: string; // WCO-2544: in-progress allowance % text
}

const toEditableRows = (materials: LabourFactorMaterial[]): EditableRow[] =>
  materials.map((m) => ({
    ...m,
    _input: m.standard_throughput_lbs_per_worker_hour != null
      ? Number(m.standard_throughput_lbs_per_worker_hour).toFixed(2)
      : '',
    _allowance: m.allowance_pct != null && Number(m.allowance_pct) > 0
      ? String(Number(m.allowance_pct))
      : '',
  }));

const LabourFactorManagement: React.FC = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const updatedBy = user?.employee_number || user?.username || undefined;

  // WCO-2525: an LF edit changes the DERIVED labour factor / baseline throughput that other
  // views cache. Invalidate those caches so they refetch the new standard without a full page
  // refresh — ['rawMaterials'] feeds the session-creation "Max for the day" estimator, and
  // ['io-mapping-materials'] feeds the Input/Output Mapping "Labor Factor" column.
  const invalidateLabourFactorConsumers = React.useCallback(() => {
    queryClient.invalidateQueries(['rawMaterials']);
    queryClient.invalidateQueries(['io-mapping-materials']);
  }, [queryClient]);

  // Product list (reused source — same as Input/Output Mapping)
  const [products, setProducts] = useState<InputOutputProduct[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [productSearch, setProductSearch] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);

  // Materials for the selected product
  const [rows, setRows] = useState<EditableRow[]>([]);
  const [loadingMaterials, setLoadingMaterials] = useState(false);
  const [materialsError, setMaterialsError] = useState<string | null>(null);
  const [savingItem, setSavingItem] = useState<string | null>(null);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // WCO-2524: change-log popover state (which material's history is open + its rows)
  const [historyItem, setHistoryItem] = useState<string | null>(null);
  const [historyRows, setHistoryRows] = useState<LabourFactorHistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Fetch products once on mount
  useEffect(() => {
    let alive = true;
    setLoadingProducts(true);
    inputOutputMappingService
      .getProducts()
      .then((data) => { if (alive) setProducts(data); })
      .catch(() => { if (alive) setProducts([]); })
      .finally(() => { if (alive) setLoadingProducts(false); });
    return () => { alive = false; };
  }, []);

  // Fetch materials whenever the selected product changes
  const loadMaterials = React.useCallback(async (productItem: string) => {
    setLoadingMaterials(true);
    setMaterialsError(null);
    try {
      const res = await productionService.getLabourFactorMaterials(productItem);
      setRows(toEditableRows(res.materials || []));
    } catch (err: any) {
      setMaterialsError(err?.response?.data?.error || err?.message || 'Error');
      setRows([]);
    } finally {
      setLoadingMaterials(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedProduct) { setRows([]); return; }
    loadMaterials(selectedProduct);
  }, [selectedProduct, loadMaterials]);

  // Auto-dismiss toast
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(timer);
  }, [toast]);

  const filteredProducts = useMemo(() => {
    if (!productSearch.trim()) return products;
    const q = productSearch.toLowerCase();
    return products.filter(
      (p) =>
        p.item_number.toLowerCase().includes(q) ||
        (p.item_english_name || '').toLowerCase().includes(q) ||
        (p.item_chinese_name || '').toLowerCase().includes(q)
    );
  }, [products, productSearch]);

  const selectedProductInfo = products.find((p) => p.item_number === selectedProduct);

  // Live baseline throughput = the (possibly-unsaved) lb/hr of the baseline row.
  const baselineThroughput = useMemo(() => {
    const baseRow = rows.find((r) => r.is_lf_baseline);
    return baseRow ? parseThroughput(baseRow._input) : null;
  }, [rows]);

  const handleInputChange = (rawItem: string, value: string) => {
    setRows((prev) => prev.map((r) => (r.raw_material_item_number === rawItem ? { ...r, _input: value } : r)));
  };

  // Persist a throughput change on blur (empty → clear with null; positive number → set).
  const handleInputBlur = async (row: EditableRow) => {
    if (!selectedProduct) return;
    const saved = row.standard_throughput_lbs_per_worker_hour;
    const parsed = parseThroughput(row._input);
    if (row._input.trim() !== '' && parsed == null) {
      setToast({ type: 'error', message: t('labourFactor.invalidThroughput') });
      return;
    }
    const savedNum = saved != null ? Number(saved) : null;
    const unchanged =
      (parsed == null && savedNum == null) ||
      (parsed != null && savedNum != null && parsed.toFixed(2) === savedNum.toFixed(2));
    if (unchanged) return; // no visible (2dp) change

    setSavingItem(row.raw_material_item_number);
    try {
      await productionService.updateLabourFactorThroughput(
        selectedProduct,
        row.raw_material_item_number,
        parsed, // null clears it
        updatedBy
      );
      setRows((prev) =>
        prev.map((r) =>
          r.raw_material_item_number === row.raw_material_item_number
            ? { ...r, standard_throughput_lbs_per_worker_hour: parsed, _input: parsed != null ? parsed.toFixed(2) : '' }
            : r
        )
      );
      invalidateLabourFactorConsumers();
      setToast({ type: 'success', message: t('labourFactor.saved') });
    } catch (err: any) {
      setToast({ type: 'error', message: err?.response?.data?.error || t('labourFactor.saveFailed') });
    } finally {
      setSavingItem(null);
    }
  };

  const handleAllowanceChange = (rawItem: string, value: string) => {
    setRows((prev) => prev.map((r) => (r.raw_material_item_number === rawItem ? { ...r, _allowance: value } : r)));
  };

  // WCO-2544: persist an allowance % change on blur (empty → 0). Planning-only — does not touch LF.
  const handleAllowanceBlur = async (row: EditableRow) => {
    if (!selectedProduct) return;
    const parsed = parseAllowance(row._allowance);
    if (parsed == null) {
      setToast({ type: 'error', message: t('labourFactor.invalidAllowance') });
      return;
    }
    const savedNum = row.allowance_pct != null ? Number(row.allowance_pct) : 0;
    if (parsed === savedNum) return; // no change

    setSavingItem(row.raw_material_item_number);
    try {
      await productionService.updateLabourFactorAllowance(
        selectedProduct,
        row.raw_material_item_number,
        parsed,
        updatedBy
      );
      setRows((prev) =>
        prev.map((r) =>
          r.raw_material_item_number === row.raw_material_item_number
            ? { ...r, allowance_pct: parsed, _allowance: parsed > 0 ? String(parsed) : '' }
            : r
        )
      );
      invalidateLabourFactorConsumers();
      setToast({ type: 'success', message: t('labourFactor.allowanceSaved') });
    } catch (err: any) {
      setToast({ type: 'error', message: err?.response?.data?.error || t('labourFactor.saveFailed') });
    } finally {
      setSavingItem(null);
    }
  };

  // Move the baseline flag to a row, then refetch the server-recomputed truth.
  const handleBaselineChange = async (row: EditableRow) => {
    if (!selectedProduct || row.is_lf_baseline) return;
    setSavingItem(row.raw_material_item_number);
    try {
      await productionService.setLabourFactorBaseline(selectedProduct, row.raw_material_item_number, updatedBy);
      await loadMaterials(selectedProduct);
      invalidateLabourFactorConsumers();
      setToast({ type: 'success', message: t('labourFactor.baselineSet') });
    } catch (err: any) {
      setToast({ type: 'error', message: err?.response?.data?.error || t('labourFactor.saveFailed') });
    } finally {
      setSavingItem(null);
    }
  };

  // WCO-2524: open the change log for one input and fetch its history (newest first).
  const openHistory = React.useCallback(async (rawItem: string) => {
    if (!selectedProduct) return;
    setHistoryItem(rawItem);
    setHistoryLoading(true);
    setHistoryRows([]);
    try {
      const res = await productionService.getLabourFactorHistory(selectedProduct, rawItem);
      setHistoryRows(res.history || []);
    } catch {
      setHistoryRows([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [selectedProduct]);

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="mb-4">
        <p className="text-sm text-gray-500 dark:text-gray-400">{t('labourFactor.subtitle')}</p>
      </div>

      {/* Toast */}
      {toast && (
        <div
          className={`mb-3 px-4 py-2 rounded-md text-sm ${
            toast.type === 'success'
              ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300'
              : 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300'
          }`}
        >
          {toast.message}
        </div>
      )}

      {/* Two-panel layout */}
      <div className="flex-1 flex flex-col md:flex-row gap-4 min-h-0">
        {/* Left: products */}
        <div className="w-full md:w-1/3 flex flex-col bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="p-3 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              {t('labourFactor.selectProduct')}
            </h3>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                placeholder={t('labourFactor.searchProducts')}
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loadingProducts ? (
              <div className="p-4 text-center text-sm text-gray-500">{t('common.loading')}</div>
            ) : filteredProducts.length === 0 ? (
              <div className="p-4 text-center text-sm text-gray-500">{t('labourFactor.noResults')}</div>
            ) : (
              filteredProducts.map((p) => (
                <button
                  key={p.item_number}
                  onClick={() => setSelectedProduct(p.item_number)}
                  className={`w-full text-left px-3 py-2.5 border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                    selectedProduct === p.item_number
                      ? 'bg-primary-50 dark:bg-primary-900/30 border-l-2 border-l-primary-500'
                      : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {p.item_english_name || p.item_number}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {p.item_number}
                        {p.item_chinese_name && ` · ${p.item_chinese_name}`}
                      </div>
                    </div>
                    <span className="ml-2 flex-shrink-0 px-1.5 py-0.5 text-xs font-medium bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 rounded">
                      {p.raw_material_count}
                    </span>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Right: materials table */}
        <div className="flex-1 flex flex-col bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          {!selectedProduct ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center p-8 max-w-md">
                <Scale className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('labourFactor.selectProductPrompt')}
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
                <button
                  onClick={() => setSelectedProduct(null)}
                  className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded flex-shrink-0"
                >
                  <X className="h-4 w-4" />
                </button>
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                    {t('labourFactor.materials')}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                    {selectedProductInfo?.item_english_name || selectedProduct}
                    {selectedProductInfo?.item_chinese_name && ` (${selectedProductInfo.item_chinese_name})`}
                  </p>
                </div>
              </div>

              {/* Table */}
              <div className="flex-1 overflow-auto">
                {loadingMaterials ? (
                  <div className="p-4 text-center text-sm text-gray-500">{t('common.loading')}</div>
                ) : materialsError ? (
                  <div className="p-4 text-center text-sm text-red-500">{materialsError}</div>
                ) : rows.length === 0 ? (
                  <div className="flex items-center justify-center p-8 text-gray-400 dark:text-gray-500">
                    <div className="text-center">
                      <ArrowRightLeft className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">{t('labourFactor.noMaterials')}</p>
                    </div>
                  </div>
                ) : (
                  <>
                    {baselineThroughput == null && (
                      <div className="px-4 py-2 text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-100 dark:border-amber-800">
                        {t('labourFactor.noBaselineHint')}
                      </div>
                    )}
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-700/50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">#</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('labourFactor.itemNumber')}</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('labourFactor.name')}</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('labourFactor.throughput')}</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('labourFactor.allowance')}</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('labourFactor.labourFactor')}</th>
                          <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('labourFactor.baseline')}</th>
                          <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('labourFactor.history')}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                        {rows.map((row, idx) => {
                          const lf = deriveLabourFactor(baselineThroughput, parseThroughput(row._input));
                          const hasSavedStandard = row.standard_throughput_lbs_per_worker_hour != null;
                          const isSaving = savingItem === row.raw_material_item_number;
                          return (
                            <tr key={row.raw_material_item_number} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                              <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{row.preference ?? idx + 1}</td>
                              <td className="px-3 py-2 font-mono text-xs text-gray-700 dark:text-gray-300">
                                {row.raw_material_item_number}
                              </td>
                              <td className="px-3 py-2">
                                <div className="text-gray-900 dark:text-gray-100">{row.raw_material_name || '—'}</div>
                                {row.raw_material_chinese_name && (
                                  <div className="text-xs text-gray-500 dark:text-gray-400">{row.raw_material_chinese_name}</div>
                                )}
                              </td>
                              <td className="px-3 py-2 text-right">
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  aria-label={`${t('labourFactor.throughput')} ${row.raw_material_item_number}`}
                                  value={row._input}
                                  disabled={isSaving}
                                  onChange={(e) => handleInputChange(row.raw_material_item_number, e.target.value)}
                                  onBlur={() => handleInputBlur(row)}
                                  className="w-24 px-2 py-1 text-right text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50"
                                />
                              </td>
                              <td className="px-3 py-2 text-right">
                                <input
                                  type="number"
                                  min="0"
                                  max="100"
                                  step="1"
                                  aria-label={`${t('labourFactor.allowance')} ${row.raw_material_item_number}`}
                                  placeholder="0"
                                  value={row._allowance}
                                  disabled={isSaving}
                                  onChange={(e) => handleAllowanceChange(row.raw_material_item_number, e.target.value)}
                                  onBlur={() => handleAllowanceBlur(row)}
                                  className="w-20 px-2 py-1 text-right text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50"
                                />
                              </td>
                              <td className="px-3 py-2 text-right font-medium text-gray-700 dark:text-gray-300">
                                {lf != null ? lf.toFixed(2) : '—'}
                              </td>
                              <td className="px-3 py-2 text-center">
                                <input
                                  type="radio"
                                  name={`lf-baseline-${selectedProduct}`}
                                  aria-label={`${t('labourFactor.baseline')} ${row.raw_material_item_number}`}
                                  checked={row.is_lf_baseline}
                                  disabled={!hasSavedStandard || isSaving}
                                  title={!hasSavedStandard ? t('labourFactor.baselineNeedsThroughput') : undefined}
                                  onChange={() => handleBaselineChange(row)}
                                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 disabled:opacity-40 disabled:cursor-not-allowed"
                                />
                              </td>
                              <td className="px-3 py-2 text-center">
                                <button
                                  type="button"
                                  aria-label={`${t('labourFactor.history')} ${row.raw_material_item_number}`}
                                  title={t('labourFactor.history')}
                                  onClick={() => openHistory(row.raw_material_item_number)}
                                  className="inline-flex items-center justify-center p-1 rounded text-gray-400 hover:text-primary-600 hover:bg-gray-100 dark:hover:bg-gray-700"
                                >
                                  <History className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* WCO-2524 change-log modal (per-material history) */}
      {historyItem && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setHistoryItem(null)}
        >
          <div
            className="w-full max-w-md rounded-lg bg-white dark:bg-gray-800 shadow-xl border border-gray-200 dark:border-gray-700"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2 min-w-0">
                <History className="w-4 h-4 text-gray-500 dark:text-gray-400 shrink-0" />
                <span className="font-medium text-gray-900 dark:text-gray-100">{t('labourFactor.historyTitle')}</span>
                <span className="font-mono text-xs text-gray-500 dark:text-gray-400 truncate">{historyItem}</span>
              </div>
              <button
                type="button"
                aria-label={t('labourFactor.close')}
                onClick={() => setHistoryItem(null)}
                className="p-1 rounded text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="max-h-80 overflow-y-auto p-2">
              {historyLoading ? (
                <div className="px-3 py-6 text-center text-sm text-gray-500 dark:text-gray-400">{t('labourFactor.historyLoading')}</div>
              ) : historyRows.length === 0 ? (
                <div className="px-3 py-6 text-center text-sm text-gray-500 dark:text-gray-400">{t('labourFactor.historyEmpty')}</div>
              ) : (
                <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                  {historyRows.map((h) => (
                    <li key={h.id} className="px-3 py-2">
                      <div className="text-sm text-gray-900 dark:text-gray-100">
                        {h.change_type === 'throughput'
                          ? `${t('labourFactor.historyThroughput')}: ${h.old_throughput != null ? Number(h.old_throughput).toFixed(2) : '—'} → ${h.new_throughput != null ? Number(h.new_throughput).toFixed(2) : '—'}`
                          : h.new_is_baseline
                            ? t('labourFactor.historyBaselineSet')
                            : t('labourFactor.historyBaselineUnset')}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {(h.changed_by || '—')} · {formatDateTimeLocal(h.changed_at)}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LabourFactorManagement;
